// Distancia y velocidad por intervalo con el odómetro limpio, para las vistas
// web de informes. Es el MISMO cálculo que el archivo:
//   apps/torre-de-control-api/functions/src/utils/intervalDistance.js
// (de ahí salen también los Informes rápidos). Si cambias uno, cambia el otro;
// la evidencia medida de cada constante está comentada allá.

// Ningún camión de la flota medida pasa de 122 km/h; 250 descarta saltos GPS.
export const MAX_PLAUSIBLE_KMH = 250;
// Retraso tolerado del odómetro (Mapon sube a escalones atrasados).
const ODO_LAG_H = 1;
// Retroceso menor a esto es ruido de redondeo; mayor, un reseteo o basura.
const ODO_NOISE_BACK_KM = 1;
// Δt mínimo para estimar una velocidad (10 m en 1 s ya serían 36 km/h).
const MIN_IMPLIED_DT_MS = 3000;
// IDLE_CONFIG.maxGapMs del backend: más allá no se estima velocidad.
const MAX_IMPLIED_DT_MS = 15 * 60 * 1000;

export interface IntervalPoint {
  ts: number;
  lat: number | null;
  lon: number | null;
  speed?: number | null;
  odoKm: number | null;
}

interface OdoChange {
  idx: number;
  cumKm: number;
  reset: boolean;
}

const num = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
};

/** resolveOdoKm del backend: odo_km/odometer_km en km, o los odómetros en metros. */
export const resolveOdoKm = (params: Record<string, unknown> | null | undefined): number | null => {
  if (!params) return null;
  const km = num(params.odo_km ?? params.odometer_km);
  if (km != null) return km;
  const meters = num(params.virtual_odometer ?? params.total_odometer ?? params.odometer);
  return meters == null ? null : meters / 1000;
};

const validCoord = (p: IntervalPoint): boolean =>
  p.lat !== null && p.lon !== null && (p.lat !== 0 || p.lon !== 0);

/** calculateDistance del backend (haversine, km). */
const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** cleanOdometer del backend: cambios de odómetro aceptados (sin picos, con reseteos). */
export const cleanOdometer = (points: IntervalPoint[]): { changes: OdoChange[]; anchorAt: (number | null)[] } => {
  const changes: OdoChange[] = [];
  const anchorAt: (number | null)[] = new Array(points.length).fill(null);
  type Anchor = { odo: number; ts: number };
  let anchor: Anchor | null = null;
  let prev: { anchor: Anchor; cum: number } | null = null;
  let pending: Anchor | null = null;
  let cum = 0;
  let resetSinceAnchor = false;
  const plausible = (from: Anchor, odo: number, ts: number) => {
    const delta = odo - from.odo;
    const hours = Math.max(0, ts - from.ts) / 3600000;
    return delta >= -ODO_NOISE_BACK_KM && delta <= MAX_PLAUSIBLE_KMH * (hours + ODO_LAG_H);
  };
  for (let i = 0; i < points.length; i++) {
    if (i > 0) anchorAt[i] = anchorAt[i - 1];
    const { odoKm: odo, ts } = points[i];
    if (odo == null || odo <= 0) continue;
    if (!anchor) {
      anchor = { odo, ts };
      anchorAt[i] = odo;
      changes.push({ idx: i, cumKm: 0, reset: false });
      continue;
    }
    if (!plausible(anchor, odo, ts) && odo < anchor.odo && prev && plausible(prev.anchor, odo, ts)) {
      changes.pop();
      ({ anchor, cum } = prev);
      prev = null;
      resetSinceAnchor = true;
    }
    if (plausible(anchor, odo, ts)) {
      pending = null;
      if (odo > anchor.odo) {
        prev = { anchor, cum };
        cum += odo - anchor.odo;
        anchor = { odo, ts };
        changes.push({ idx: i, cumKm: cum, reset: resetSinceAnchor });
        resetSinceAnchor = false;
      }
      anchorAt[i] = anchor.odo;
      continue;
    }
    if (pending && plausible(pending, odo, ts)) {
      cum += Math.max(0, odo - pending.odo);
      anchor = odo > pending.odo ? { odo, ts } : pending;
      anchorAt[i] = anchor.odo;
      prev = null;
      pending = null;
      changes.push({ idx: i, cumKm: cum, reset: true });
      resetSinceAnchor = false;
      continue;
    }
    pending = { odo, ts };
  }
  return { changes, anchorAt };
};

/**
 * intervalDistances del backend: km de cada intervalo (dist[i] = de i-1 a i),
 * el odómetro limpio repartido por GPS (o por tiempo) y GPS donde no hay.
 */
export const intervalDistances = (
  points: IntervalPoint[],
  odo: { changes: OdoChange[] },
  dayStartMs: number,
  moving: boolean[] | null = null,
): { dist: number[]; byOdo: boolean[] } => {
  const n = points.length;
  const gps: number[] = new Array(n).fill(0);
  for (let i = 1; i < n; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!validCoord(a) || !validCoord(b)) continue;
    const km = haversineKm(a.lat as number, a.lon as number, b.lat as number, b.lon as number);
    const hours = (b.ts - a.ts) / 3600000;
    if (km > 1 && (hours <= 0 || km / hours > MAX_PLAUSIBLE_KMH)) continue;
    gps[i] = km;
  }
  const dist = gps.slice();
  const byOdo: boolean[] = new Array(n).fill(false);
  const { changes } = odo;
  let carryKm = 0;
  for (let k = 1; k < changes.length; k++) {
    if (changes[k].reset) continue;
    const from = changes[k - 1].idx;
    const to = changes[k].idx;
    const odoKm = Math.max(0, changes[k].cumKm - changes[k - 1].cumKm - carryKm);
    carryKm = 0;
    const lagged =
      points[from].ts < dayStartMs && points[to].ts >= dayStartMs && points[to].ts - dayStartMs <= ODO_LAG_H * 3600000;
    if (lagged) {
      for (let i = from + 1; i <= to; i++) {
        byOdo[i] = true;
        if (points[i - 1].ts >= dayStartMs) carryKm += gps[i];
      }
      continue;
    }
    const counts = (i: number) => !moving || moving[i];
    let gpsKm = 0;
    let spanMs = 0;
    for (let i = from + 1; i <= to; i++) {
      byOdo[i] = true;
      dist[i] = 0;
      if (!counts(i)) continue;
      gpsKm += gps[i];
      spanMs += points[i].ts - points[i - 1].ts;
    }
    let shareKm = odoKm;
    if (moving) {
      const cap = spanMs > 0 ? Math.max(gpsKm * 1.5, gpsKm + 1) : 0;
      let j = from;
      while (j > 0 && !moving[j]) j--;
      if (j > 0 && odoKm > cap) {
        dist[j] += odoKm - cap;
        byOdo[j] = true;
        shareKm = cap;
      }
    }
    for (let i = from + 1; i <= to; i++) {
      if (spanMs > 0 && !counts(i)) continue;
      const dt = points[i].ts - points[i - 1].ts;
      const total = spanMs || points[to].ts - points[from].ts;
      dist[i] = gpsKm > 0 ? (gps[i] * shareKm) / gpsKm : total > 0 ? (shareKm * dt) / total : 0;
    }
  }
  return { dist, byOdo };
};

/** estimateSpeeds del backend: la reportada o, si viene vacía, la del intervalo; 0 si no se puede. */
export const estimateSpeeds = (points: IntervalPoint[], dist: number[]): number[] => {
  const implied = (k: number): number | null => {
    if (k < 1 || k >= points.length) return null;
    const dt = points[k].ts - points[k - 1].ts;
    return dt < MIN_IMPLIED_DT_MS || dt > MAX_IMPLIED_DT_MS ? null : dist[k] / (dt / 3600000);
  };
  return points.map((p, i) => (p.speed != null ? p.speed : implied(i) ?? implied(i + 1) ?? 0));
};

// Constantes de dropGpsDrift / isDisplaced del backend (evidencia allá).
const NO_DATA_GAP_MS = 30 * 60 * 1000;
const GAP_MOVING_KMH = 10;
const MIN_MOVE_M = 75;
const MOVE_WINDOW_MS = 5 * 60 * 1000;
const MOVEMENT_KMH = 3;

/** dropGpsDrift del backend: GPS sin odómetro que lo respalde en una unidad quieta es deriva. */
export const dropGpsDrift = (
  points: IntervalPoint[],
  dist: number[],
  byOdo: boolean[],
  movingAt: (k: number) => boolean,
  gapMs = NO_DATA_GAP_MS,
): number[] => {
  for (let i = 1; i < points.length; i++) {
    const dt = points[i].ts - points[i - 1].ts;
    const gap = dt > gapMs;
    if (!byOdo[i] && (gap ? dist[i] / (dt / 3600000) < GAP_MOVING_KMH : !movingAt(i - 1))) dist[i] = 0;
  }
  return dist;
};

/** isDisplaced del backend: ¿se alejó el punto i más de MIN_MOVE_M de otro de ±MOVE_WINDOW_MS? */
export const isDisplaced = (points: IntervalPoint[], anchorAt: (number | null)[], i: number): boolean => {
  const anchorKm = (k: number) => anchorAt[k] ?? anchorAt[i];
  for (const step of [-1, 1]) {
    for (let j = i + step; j >= 0 && j < points.length && (j === i + step || Math.abs(points[j].ts - points[i].ts) <= MOVE_WINDOW_MS); j += step) {
      const gpsM =
        validCoord(points[i]) && validCoord(points[j])
          ? haversineKm(points[i].lat as number, points[i].lon as number, points[j].lat as number, points[j].lon as number) * 1000
          : 0;
      const odoM = Math.abs((anchorKm(j) ?? 0) - (anchorKm(i) ?? 0)) * 1000;
      if (Math.max(gpsM, odoM) > MIN_MOVE_M) return true;
    }
  }
  return false;
};

/**
 * rangeDistanceKm del backend: km recorridos desde `startMs` (odómetro limpio,
 * GPS donde no hay odómetro, sin deriva). «Lectura final − inicial» pasaba
 * entero un reseteo o un valor basura (GL 31, 15-oct: 944 km contra 857 reales).
 */
export const rangeDistanceKm = (points: IntervalPoint[], startMs: number): number => {
  if (points.length < 2) return 0;
  const odo = cleanOdometer(points);
  const { dist, byOdo } = intervalDistances(points, odo, startMs);
  const speeds = estimateSpeeds(points, dist);
  dropGpsDrift(points, dist, byOdo, (k) => speeds[k] > MOVEMENT_KMH && isDisplaced(points, odo.anchorAt, k));
  let km = 0;
  for (let i = 1; i < points.length; i++) if (points[i - 1].ts >= startMs) km += dist[i];
  return km;
};
